import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import {
    DatasetAdapter,
    DatasetMetadata,
    toDatasetRef,
} from '../adapters/types';
import { bindDataset, showImageRows } from '../layout/mutations';
import { Layout, ViewportIdFactory } from '../layout/types';
import {
    DEFAULT_ROW_TEMPLATE,
    protocolOfLayout,
    SIDE_BY_SIDE,
} from './hanging';
import { useAvailableDatasets } from './useAvailableDatasets';

/**
 * Binding orchestration: resolves a series through the dataset adapter and
 * binds it into a row. The adapter serves one patient, so every dataset it
 * resolves may be bound; a series it does not resolve binds nothing. The
 * pure pieces live in `hanging`/`mutations`; this only sequences them.
 */

export interface DatasetBinding {
    /** The datasets a row picker offers: every dataset the adapter serves. */
    readonly availableDatasets: ReadonlyArray<DatasetMetadata>;
    requestBind(rowIndex: number, seriesInstanceUid: SeriesInstanceUid): void;
    /**
     * Shows each of `seriesInstanceUids` in a row of its own, as
     * `showImageRows` does; a series the adapter does not resolve gets no
     * row.
     */
    requestRows(seriesInstanceUids: ReadonlyArray<SeriesInstanceUid>): void;
}

export function useDatasetBinding(
    setLayout: (update: (previous: Layout) => Layout) => void,
    datasetAdapter: DatasetAdapter,
    makeId: ViewportIdFactory
): DatasetBinding {
    const availableDatasets = useAvailableDatasets(datasetAdapter);

    // A new row hangs the shape the grid already hangs, so every row reads
    // the same way; the default stands in once the rows hang their own ways.
    // Side by side is a view of a fused pair, and a new row has nothing
    // fused, so it starts in the default too rather than beside an empty
    // pane.
    const newRowTemplate = (previous: Layout) => {
        const shared = protocolOfLayout(previous)?.template;
        return shared !== undefined && shared !== SIDE_BY_SIDE
            ? shared
            : DEFAULT_ROW_TEMPLATE;
    };

    const requestBind = (
        rowIndex: number,
        seriesInstanceUid: SeriesInstanceUid
    ) => {
        const metadata = datasetAdapter.resolve(seriesInstanceUid)?.metadata;
        if (!metadata) {
            return;
        }
        setLayout((previous) =>
            bindDataset(
                previous,
                rowIndex,
                toDatasetRef(metadata),
                newRowTemplate(previous),
                makeId
            )
        );
    };

    const requestRows = (
        seriesInstanceUids: ReadonlyArray<SeriesInstanceUid>
    ) => {
        const datasets = seriesInstanceUids.flatMap((uid) => {
            const metadata = datasetAdapter.resolve(uid)?.metadata;
            return metadata ? [toDatasetRef(metadata)] : [];
        });
        setLayout((previous) =>
            showImageRows(previous, datasets, newRowTemplate(previous), makeId)
        );
    };

    return { availableDatasets, requestBind, requestRows };
}
